/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import parsers from '@sportninja/common/reducers/parsers'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { connect } from 'react-redux'
import { useBalanceRosters } from '@sportninja/common/features/team-balance/hooks/useBalanceRosters'
import { useTeamPlayerFinderSettings } from '@sportninja/common/features/team/playerFinder/hooks/useTeamPlayerFinderSettings'
import RegistrationTab from 'src/features/team-balance/RegistrationTab'
import { teamPageTabGates } from 'src/features/team-balance/utils/teamTabsGate'
import Page from '../../components/Page'
import FormSheet from '../../components/Form/FormSheet'
import { feedItemForm } from '../../components/Form/form-configs'
import FavouriteButton from '../../components/Page/FavouriteButton'
import UnifiedRoster from './UnifiedRoster'
import PlayerSearchModal from 'src/features/team/player/components/PlayerSearchModal'
import RosterPlayerModal from 'src/features/team/player/RosterPlayerModal'
import TeamOfficialModal from 'src/components/TeamOfficialModal'
import GameList from '../../components/List/GameList'
import NewButton from '../../components/Page/NewButton'
import FileDownload from '../../components/FileDownload'
import BaseActionButton from '../../components/Page/BaseActionButton'
import actionDropdownOverlay from '../../components/Page/actionDropdownOverlay'
import { Mobile } from '../../components/Responsive'
import EditButton from '../../components/Page/EditButton'
import ImportTeamRoster from '../../components/ImportCSV/ImportTeamRoster'
import { getBreadcrumbs, useReadItem, useStoreRecent } from '../utils'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { enablePlayerTeamFinder } from '@sportninja/common/utils/utils'
import {
  getAllGenericSportProperties,
  getHockeySport,
  getSoccerSport,
} from '@sportninja/common/selectors/types'
import { NewModal } from 'src/components/NewModal'
import { Button } from 'src/components/Button'
import calendarImg from './calendar.svg'
import outlookIcon from './outlook-icon.svg'
import calendarIcon from './calendar-icon.svg'
import colors from '@sportninja/common/constants/appColors'
import Icon from 'src/components/Icon'
import { Dropdown } from 'antd'
import ExportIcon from 'src/components/icons/ExportIcon'
import ImportIcon from 'src/components/icons/ImportIcon'
import { Input } from 'src/components/Input'
import TeamStatistics from 'src/components/v2/TeamStatistics'
import { useSports } from '@sportninja/common/hooks/useSports'
import GeneratePluginModal from 'src/components/GeneralPluginModal'
import {
  resolveSyncSalesforce,
  shouldAllowAddPlayer,
  shouldAllowAddImportRoster,
  shouldAllowAddTeamOfficials,
} from '@sportninja/common/ts-utils/add-roster-utils'
import EntityFeed from 'src/components/EntityFeed'
import TeamModal from 'src/components/TeamModal'
import { useEventModal } from '@sportninja/common/features/event/hooks/useEventModal'
import EventModal from 'src/features/event/components/EventModal'
import { resolveEventFormScheduleId } from '@sportninja/common/features/event/eventOwnership'
import { getAddEventDisabledReason } from '@sportninja/common/features/event/addEventDisabledReason'
import RoleBadge from 'src/components/ts-components/RoleBadge'

const Team = ({
  match,
  soccerSportId,
  hockeySportId,
  genericSportProperties,
  authUserId,
}) => {
  const history = useHistory()
  const [rosterId, setRosterId] = useState('')
  // The roster GameList resolved for the selected season/division. Separate
  // from `rosterId` above, which only gets set once the Roster tab has mounted
  // — landing straight on /schedule would otherwise leave Add Event hidden.
  const [scheduleRosterId, setScheduleRosterId] = useState('')
  const [pendingHighlightId, setPendingHighlightId] = useState(null)
  const [shouldShowWaiver, setShouldShowWaiver] = useState(false)
  // Roster's require_email flag → makes the player email mandatory in the modal.
  const [requireEmail, setRequireEmail] = useState(false)
  const [areFiltersHidden, setAreFiltersHidden] = useState(false)
  const [filterForExporting, setFilterForExporting] = useState(null)
  // "Mighty Drunks" + "team_officials" -> "Mighty_Drunks_team_officials"
  // (FileDownload appends .csv). Spaces become underscores so the name is
  // one token everywhere it lands.
  const rosterExportFileName = (suffix) =>
    `${(item?.name_full || 'team').trim().replace(/\s+/g, '_')}_${suffix}`
  const [calendarBlob, setCalendarBlob] = useState(null)
  const [calendarURL, setCalendarURL] = useState(null)
  const [hasCopiedLink, setHasCopiedLink] = useState(false)
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false)
  const [rosterActiveTab, setRosterActiveTab] = useState('players')
  const [isOfficialModalOpen, setIsOfficialModalOpen] = useState(false)
  // Add Player flow: search modal → roster modal. selectedPlayerId set means
  // add-existing (prefill + copy); undefined means create-new (blank).
  const [isPlayerSearchOpen, setIsPlayerSearchOpen] = useState(false)
  const [isRosterModalOpen, setIsRosterModalOpen] = useState(false)
  const [selectedPlayerId, setSelectedPlayerId] = useState(undefined)
  // Add-existing: the picked search row (name/email/image), seeds the modal.
  const [selectedSearchPlayer, setSelectedSearchPlayer] = useState(undefined)
  // Player ids already on the roster — fed to the search modal so a player
  // already on the team is filtered out of the results (can't be added twice).
  const [rosterPlayerIds, setRosterPlayerIds] = useState([])
  const { sports, fetchSports } = useSports()

  useEffect(() => {
    if (!sports || sports?.length === 0) {
      fetchSports()
    }
  }, [fetchSports, sports])

  const [lockRosterDate, setLockRosterDate] = useState(null)
  const [canAddPlayersFromRoster, setCanAddPlayersFromRoster] = useState(false)
  const [isRegistrationOn, setIsRegistrationOn] = useState(false)
  const [rosterPermission, setRosterPermission] = useState({})
  const isCurrentDateAfterDivisionOrCompetitionLockDate = useMemo(() => {
    if (!lockRosterDate) {
      return false
    }
    if (lockRosterDate) {
      return new Date(lockRosterDate) < new Date()
    }
  }, [lockRosterDate])
  // get the sn_schedule from the url
  const urlParams = new URLSearchParams(window.location.search)
  const scheduleId = urlParams.get('sn_schedule')

  const {
    loading,
    error,
    item,
    hierarchy,
    permission,
    roles,
    readItem,
    refreshKey,
    triggerRefresh,
  } = useReadItem()

  // Events tab modal state
  const eventModal = useEventModal()

  const sport = useMemo(() => {
    return sports?.find((s) => s.id === item?.sport?.id)
  }, [sports, item?.sport?.id])

  const genericSport = useMemo(() => {
    if (item?.generic_sport_id) {
      return genericSportProperties.find((s) => s.id === item.generic_sport_id)
    }
    return null
  }, [item, genericSportProperties])
  const isGenericSport = genericSport !== null && genericSport?.id !== null

  const sync_salesforce = resolveSyncSalesforce(item)

  const loadCalendarICS = useCallback(async () => {
    const res = await req(`/calendar/${TEAM_ID}/games`, {
      parseJSON: false,
    })

    if (res?.url) {
      setCalendarURL(res.url)
      setCalendarBlob(await res.blob())
    }
  }, [])

  const TEAM_ID = match.params.id?.replace('?sandbox_tour=1', '')
  const BASE_ROUTE = `${ROUTES.TEAM_ROOT}/${TEAM_ID}`
  const IS_PUBLIC = item.is_public
  const isSoccer = item?.sport?.id === soccerSportId
  const isHockey = item?.sport?.id === hockeySportId

  // SN-4709 Balance tab probe (D25 as amended by D32 — tenant-neutral, no
  // isCanlan and no permission.update, which would hide the tab from bucket
  // reps). D40: the sheet is roster-scoped, so the team-wide "do I have any
  // balance here?" question the gate needs is now answered by roster DISCOVERY.
  // The probe waits for auth + the loaded team, shares its react-query key with
  // the tab's roster switcher (one fetch), and a 403/empty result silently means
  // "no tab". authUserId arrives via connect — this repo's react-redux predates
  // useSelector — and also keys the per-caller discovery cache.
  const { rosters: balanceRosters } = useBalanceRosters({
    teamId: TEAM_ID,
    authUserId,
    enabled:
      Boolean(authUserId) && Boolean(item?.id) && item?.is_placeholder !== true,
  })
  // SN-4709 × SN-8081 — the Player Finder sub-tab's probe: the settings GET
  // itself, under the same waits as the balance probe. It shares its react-query
  // key with the sub-tab (one fetch), a 403 silently means "no sub-tab", and it
  // is budget-independent, so a team with no Team-Managed Budget still gets a
  // Registration tab when its rep can manage an ad. SN-8453 — skipped where
  // Player Finder is off (SportNinja): the sub-tab can never show there.
  const { registrations: playerFinderRegistrations } =
    useTeamPlayerFinderSettings({
      teamId: TEAM_ID,
      authUserId,
      enabled:
        enablePlayerTeamFinder &&
        Boolean(authUserId) &&
        Boolean(item?.id) &&
        item?.is_placeholder !== true,
    })
  const tabGates = teamPageTabGates({
    item,
    permissionUpdate: Boolean(permission.update),
    balanceRegistrations: balanceRosters,
    playerFinderRegistrations,
    playerFinderEnabled: enablePlayerTeamFinder,
  })

  const calendarSyncModalRef = useRef()
  const generatePluginTagModalRef = useRef(null)
  // Set by GameList; jumps the schedule to the period holding a saved event.
  const followEventRef = useRef(null)

  useStoreRecent(ENTITY_TYPES.team, item, error, TEAM_ID, false)

  useEffect(() => {
    readItem(ENTITY_TYPES.team, TEAM_ID)
  }, [TEAM_ID])

  const shouldEnableAddPlayer = useMemo(() => {
    return shouldAllowAddPlayer({
      adminPermission: rosterPermission.admin,
      updatePermission: rosterPermission.update,
      rosterId,
      sync_salesforce,
      organization: item.organization,
      isCurrentDateAfterDivisionOrCompetitionLockDate,
      canAddPlayersFromRoster,
      isRegistrationOn,
    })
  }, [
    canAddPlayersFromRoster,
    isCurrentDateAfterDivisionOrCompetitionLockDate,
    isRegistrationOn,
    item.organization,
    rosterPermission,
    rosterId,
    sync_salesforce,
  ])

  // Derive a reason to show in a tooltip when Add Player is disabled
  const addPlayerDisabledReason = useMemo(() => {
    if (shouldEnableAddPlayer) {
      return null
    }
    // Permission checks first
    if (!rosterPermission?.admin && !rosterPermission?.update) {
      return 'You Do Not Have Permission To Manage The Roster.'
    }
    if (!rosterId) {
      return 'Roster Is Not Available For This Team.'
    }
    // Registration-specific restriction
    if (isRegistrationOn && !canAddPlayersFromRoster) {
      return 'Registration Is Turned On, Add Players Via Registration.'
    }
    // Lock date restriction
    if (isCurrentDateAfterDivisionOrCompetitionLockDate) {
      return 'Roster Is Locked For This Competition.'
    }
    // Salesforce restriction (for Canlan brands)
    if (isCanlan && sync_salesforce && !rosterPermission?.admin) {
      return 'Roster Is Managed By Salesforce.'
    }
    return 'Action Unavailable.'
  }, [
    shouldEnableAddPlayer,
    rosterPermission?.admin,
    rosterPermission?.update,
    rosterId,
    isRegistrationOn,
    canAddPlayersFromRoster,
    isCurrentDateAfterDivisionOrCompetitionLockDate,
    sync_salesforce,
  ])

  const isMember = useMemo(
    () =>
      permission?.member ||
      permission?.admin ||
      permission?.update ||
      permission?.post,
    [
      permission?.member,
      permission?.admin,
      permission?.update,
      permission?.post,
    ]
  )

  const shouldEnableAddImportRoster = useMemo(() => {
    return shouldAllowAddImportRoster({
      adminPermission: rosterPermission?.admin,
      updatePermission: rosterPermission?.update,
      rosterId,
      sync_salesforce,
      isCurrentDateAfterDivisionOrCompetitionLockDate:
        isCurrentDateAfterDivisionOrCompetitionLockDate,
      isRegistrationOn,
      canAddPlayersFromRoster,
    })
  }, [
    canAddPlayersFromRoster,
    isCurrentDateAfterDivisionOrCompetitionLockDate,
    isRegistrationOn,
    rosterPermission?.admin,
    rosterPermission?.update,
    rosterId,
    sync_salesforce,
  ])

  // Derive a reason to show in a tooltip when Import Roster is disabled
  const importRosterDisabledReason = useMemo(() => {
    if (shouldEnableAddImportRoster) {
      return null
    }
    if (!rosterPermission?.admin && !rosterPermission?.update) {
      return 'You Do Not Have Permission To Import Rosters.'
    }
    if (!rosterId) {
      return 'Roster Is Not Available For This Team.'
    }
    // Registration-specific restriction
    if (isRegistrationOn && !canAddPlayersFromRoster) {
      return 'Registration Is Turned On, Add Players Via Registration.'
    }
    if (!isCanlan && isCurrentDateAfterDivisionOrCompetitionLockDate) {
      return 'Roster Is Locked For This Competition.'
    }
    if (isCanlan && sync_salesforce && !rosterPermission?.admin) {
      return 'Roster Is Managed By Salesforce.'
    }
    return 'Action Unavailable.'
  }, [
    shouldEnableAddImportRoster,
    rosterPermission?.admin,
    rosterPermission?.update,
    rosterId,
    isRegistrationOn,
    canAddPlayersFromRoster,
    isCurrentDateAfterDivisionOrCompetitionLockDate,
    sync_salesforce,
  ])

  const shouldEnableAddTeamOfficials = useMemo(() => {
    return shouldAllowAddTeamOfficials({
      rosterId,
      sync_salesforce,
      organization: item?.organization,
      adminPermission: rosterPermission?.admin,
      updatePermission: rosterPermission?.update,
    })
  }, [rosterPermission, rosterId, sync_salesforce, item?.organization])

  const titleActions = item?.id
    ? [
        <FavouriteButton
          key='favourite'
          initialState={item.is_favorite}
          entityType={ENTITY_TYPES.team}
          entityId={TEAM_ID}
        />,
        permission.update && (
          <EditButton
            key='edit'
            onClick={() => setIsTeamModalOpen(true)}
            title={item?.name_full}
          />
        ),
        // The viewer's own role(s) on this team, from `meta.roles[teamId]`.
        // A user can hold more than one, so each gets its own badge.
        ...roles.map((role) => (
          <RoleBadge
            key={`role-${role.id}`}
            roleId={role.id}
            name={role.name}
            css={roleBadgeStyles}
          />
        )),
      ]
    : []

  const baseActions = []

  const filterToggleButton = (
    <BaseActionButton
      faType={areFiltersHidden ? 'far' : 'fas'}
      key='toggle-filter'
      name='filter'
      title={`Toggle Filters Visibility ${areFiltersHidden ? 'on' : 'off'}`}
      onClick={() => {
        setAreFiltersHidden((v) => !v)
      }}
    />
  )

  const calendarSyncButton = (
    <BaseActionButton
      key='toggle-calendar-sync'
      customIcon={
        <img
          src={calendarIcon}
          css={css`
            width: 16px;
            height: 16px;
          `}
        />
      }
      title='Schedule Calendar Sync'
      onClick={async () => {
        loadCalendarICS()
        calendarSyncModalRef.current?.openModal()
      }}
    />
  )

  const generatePluginModalButton = permission?.update ? (
    <BaseActionButton
      key='plug'
      name='plug'
      title={'Generate JS Plugin Tag'}
      onClick={() => {
        generatePluginTagModalRef?.current?.openModal()
      }}
    />
  ) : null

  // Gated on the TEAM permission (from `useReadItem`, i.e.
  // `meta.permissions[TEAM_ID]`) rather than `rosterPermission`, which is only
  // populated once the Roster tab has mounted and would leave this hidden for
  // anyone landing directly on /schedule.
  const canAddScheduleEvent = Boolean(permission.update || permission.admin)

  const pages = [
    {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/schedule`,
      text: t('common:schedule'),
      component: (
        <GameList
          team_id={TEAM_ID}
          //  Details is a competition-level spreadsheet — it edits games in
          //  the context of one schedule. A team's list spans every
          //  competition the team plays in, so the grid has no single schedule
          //  to edit against here.
          showDetailsView={false}
          areFiltersHidden={areFiltersHidden}
          setFilterForExporting={setFilterForExporting}
          sportId={item?.sport?.id}
          isSoccer={isSoccer}
          isHockey={isHockey}
          scheduleId={scheduleId}
          refreshKey={refreshKey}
          onRosterIdChange={setScheduleRosterId}
          onFollowEventReady={(fn) => {
            followEventRef.current = fn
          }}
        />
      ),
      actions: [
        // Creating an event needs a roster, so this stays disabled until one
        // has resolved for the selected season/division — on All Seasons, and
        // for a team that is not in the selected competition.
        canAddScheduleEvent && (
          <NewButton
            key='add-schedule-event'
            label='ADD EVENT'
            onClick={eventModal.openAdd}
            disabled={!scheduleRosterId}
            tooltipMessage={getAddEventDisabledReason()}
          />
        ),
        generatePluginModalButton,
        calendarSyncButton,
        filterToggleButton,
      ].filter(Boolean),
    },
    !isGenericSport && {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/statistics`,
      text: t('common:statistics'),
      component: (
        <TeamStatistics
          entityType={ENTITY_TYPES.team}
          entityId={TEAM_ID}
          areFiltersHidden={areFiltersHidden}
          autoSelectMostRecentComp
          isSoccer={item?.sport?.id === soccerSportId}
          displaySearch={false}
          sportId={item?.sport?.id}
        />
      ),
      actions: [filterToggleButton],
    },
    !item?.is_placeholder && {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/roster`,
      text: t('common:roster'),
      component: (
        <UnifiedRoster
          teamId={TEAM_ID}
          teamName={item?.name_full}
          permission={rosterPermission}
          refreshKey={refreshKey}
          pendingHighlightId={pendingHighlightId}
          onPendingHighlightConsumed={() => setPendingHighlightId(null)}
          onActiveTabChange={setRosterActiveTab}
          onComplete={(data) => {
            setLockRosterDate(data.lock_roster_date)
            setCanAddPlayersFromRoster(data?.can_add_players_from_roster)
            setIsRegistrationOn(data?.is_registration_on)
            setRosterId(data.id)
            setRosterPlayerIds(
              Array.isArray(data?.players)
                ? data.players.map((p) => p.id).filter(Boolean)
                : []
            )
            setShouldShowWaiver(data.should_show_waiver)
            setRequireEmail(data?.require_email || false)
            const rp = data?.permissions?.[TEAM_ID] || {}
            setRosterPermission(rp)
          }}
          isSoccer={item?.sport?.id === soccerSportId}
          sportName={item?.sport?.name}
          sportId={item?.sport?.id}
        />
      ),
      actions: [
        // Import/Export dropdowns trigger these by id, whichever tab is open;
        // the controls render their own buttons, so they stay hidden.
        rosterId && (
          <div
            key='roster-import-export-controls'
            css={css`
              display: none;
            `}
          >
            {(rosterPermission.update || rosterPermission.admin) && (
              <ImportTeamRoster
                key='import-roster'
                buttonId='import-player-roster'
                teamId={TEAM_ID}
                teamName={item.name_full}
                teamRosterId={rosterId}
                onSuccess={triggerRefresh}
                disabled={!shouldEnableAddImportRoster}
                disabledReason={importRosterDisabledReason}
              />
            )}
            {rosterPermission.update && (
              <FileDownload
                key='download-roster'
                button={(loading, onClick) => (
                  <BaseActionButton
                    id='export-player-roster'
                    loading={loading}
                    onClick={onClick}
                    name='download'
                    title={t('common:exportPlayerRoster')}
                  />
                )}
                label={t('common:exportPlayerRoster')}
                url={`/teams/${TEAM_ID}/rosters/${rosterId}/players/export/csv`}
                fileName={rosterExportFileName('players')}
                query={filterForExporting}
              />
            )}
            {(rosterPermission.update || rosterPermission.admin) && (
              <ImportTeamRoster
                key='import-team-officials'
                buttonId='import-team-officials-roster'
                rosterType='teamOfficials'
                teamId={TEAM_ID}
                teamName={item.name_full}
                teamRosterId={rosterId}
                onSuccess={triggerRefresh}
                disabled={!shouldEnableAddTeamOfficials}
                disabledReason={importRosterDisabledReason}
              />
            )}
            {rosterPermission.update && (
              <FileDownload
                key='download-team-officials'
                button={(loading, onClick) => (
                  <BaseActionButton
                    id='export-team-officials-roster'
                    loading={loading}
                    onClick={onClick}
                    name='download'
                    title={t('common:exportTeamOfficialsRoster')}
                  />
                )}
                label={t('common:exportTeamOfficialsRoster')}
                url={`/teams/${TEAM_ID}/rosters/${rosterId}/team-officials/export/csv`}
                fileName={rosterExportFileName('team_officials')}
                query={filterForExporting}
              />
            )}
          </div>
        ),
        (rosterPermission.update || rosterPermission.admin) && rosterId && (
          <Dropdown
            key='roster-import-dropdown'
            overlayClassName={actionDropdownOverlay}
            trigger={['click']}
            placement='bottomRight'
            menu={{
              items: [
                {
                  key: 'import-player-roster',
                  label: t('common:importPlayerRoster'),
                  disabled: !shouldEnableAddImportRoster,
                  onClick: () =>
                    document.getElementById('import-player-roster')?.click(),
                },
                {
                  key: 'import-team-officials-roster',
                  label: t('common:importTeamOfficialsRoster'),
                  disabled: !shouldEnableAddTeamOfficials,
                  onClick: () =>
                    document
                      .getElementById('import-team-officials-roster')
                      ?.click(),
                },
              ],
            }}
          >
            <span
              css={css`
                display: inline-flex;
              `}
            >
              <BaseActionButton
                customIcon={<ImportIcon size={16} color={colors.WHITE} />}
              />
            </span>
          </Dropdown>
        ),
        rosterPermission.update && rosterId && (
          <Dropdown
            key='roster-export-dropdown'
            overlayClassName={actionDropdownOverlay}
            trigger={['click']}
            placement='bottomRight'
            menu={{
              items: [
                {
                  key: 'export-player-roster',
                  label: t('common:exportPlayerRoster'),
                  onClick: () =>
                    document.getElementById('export-player-roster')?.click(),
                },
                {
                  key: 'export-team-officials-roster',
                  label: t('common:exportTeamOfficialsRoster'),
                  onClick: () =>
                    document
                      .getElementById('export-team-officials-roster')
                      ?.click(),
                },
              ],
            }}
          >
            <span
              css={css`
                display: inline-flex;
              `}
            >
              <BaseActionButton
                customIcon={<ExportIcon size={16} color={colors.WHITE} />}
              />
            </span>
          </Dropdown>
        ),
        rosterActiveTab === 'players'
          ? (rosterPermission.update || rosterPermission.admin) &&
            rosterId &&
            shouldEnableAddPlayer && (
              <NewButton
                key='add-player'
                onClick={() => {
                  setSelectedPlayerId(undefined)
                  setIsPlayerSearchOpen(true)
                }}
              />
            )
          : (rosterPermission.update || rosterPermission.admin) &&
            rosterId &&
            shouldEnableAddTeamOfficials && (
              <NewButton
                key='add-official'
                onClick={() => setIsOfficialModalOpen(true)}
              />
            ),
      ],
    },
    isMember && {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/feed`,
      text: t('common:feed'),
      component: (
        <EntityFeed
          entity={item}
          entityType={ENTITY_TYPES.team}
          entityId={TEAM_ID}
          refreshKey={refreshKey}
          canPost={permission.post || permission.update || permission.admin}
          canTargetAudience={permission.update || permission.admin}
        />
      ),
      actions: [
        permission.admin && (
          <Mobile key='add-post'>
            <FormSheet
              label={t('Web:createANewPost')}
              title={t('Web:createANewPost')}
              form={feedItemForm({}, false)}
              onSubmit={async ({ imageData, ...form }) => {
                const body = JSON.stringify(form)
                const response = await req(`/teams/${TEAM_ID}/social-feed`, {
                  method: 'POST',
                  body,
                })
                if (imageData) {
                  await req(`/social-feed/${response.data.id}/image`, {
                    method: 'POST',
                    body: imageData,
                  })
                }
                triggerRefresh()
              }}
              Button={({ onClick }) => <NewButton onClick={onClick} />}
            />
          </Mobile>
        ),
      ],
    },
    // SN-4709 follow-up (Jeff) — the Registration tab folds the former standalone
    // Billing tab in under a roster/registration Command Center (the balance
    // sheet). It renders whenever ANY sub-tab gate is on (`showRegistration`);
    // RegistrationTab shows the Command Center sub-tab on showBalance, the Billing
    // sub-tab on showBilling (both tenant-neutral gates unchanged from D32) and
    // the Player Finder sub-tab on showPlayerFinder, and hides the switcher when
    // only one is visible. privatePage stays — a bucket rep's balance sheet is not
    // public.
    tabGates.showRegistration && {
      to: `${ROUTES.TEAM_ROOT}/${TEAM_ID}/registration`,
      text: t('common:registration'),
      privatePage: true,
      component: (
        <RegistrationTab
          teamId={TEAM_ID}
          authUserId={authUserId}
          canPost={permission.post || permission.update || permission.admin}
          canEditBilling={tabGates.showBilling}
          showCommandCenter={tabGates.showBalance}
          showPlayerFinder={tabGates.showPlayerFinder}
          sportId={item?.sport?.id ?? null}
          sportName={item?.sport?.name}
          canManageRoster={permission.admin || permission.update}
        />
      ),
      actions: [],
    },
  ]

  return (
    <>
      <Page
        contactInfo={{
          email: item?.email,
          website: item?.website,
          phone: item?.phone,
          support_email: item?.support_email,
          support_website: item?.support_website,
          support_phone: item?.support_phone,
        }}
        sport={sport}
        genericSport={genericSport}
        isSoccer={isSoccer}
        backgroundImageUrl={resolveAssetUrl(item?.background_image)}
        isTeamOrSchedule
        isTeam={true}
        error={error}
        loading={loading}
        baseCrumb={
          error
            ? ROUTES.TEAMS.ROOT
            : `${ROUTES.ORG_ROOT}/${item?.organization?.id}/teams`
        }
        baseRoute={BASE_ROUTE}
        baseTitle={item?.organization?.name_full}
        breadcrumbs={getBreadcrumbs(ROUTES.TEAM_ROOT, item, hierarchy)}
        baseActions={baseActions}
        pages={pages}
        isPrivate={!IS_PUBLIC}
        title={item.name_full}
        subTitle={item?.generic_sport_name ? item?.generic_sport_name : null}
        organizationId={item?.organization?.id}
        iconName='user-friends'
        imageId={getImageThumbnailId(item)}
        titleActions={titleActions}
        preserveQueryFor={[
          `${ROUTES.TEAM_ROOT}/${TEAM_ID}/schedule`,
          `${ROUTES.TEAM_ROOT}/${TEAM_ID}/roster`,
          `${ROUTES.TEAM_ROOT}/${TEAM_ID}/statistics`,
          `${ROUTES.TEAM_ROOT}/${TEAM_ID}/registration`,
        ]}
      />
      <NewModal shouldCloseOnOverlayClick ref={calendarSyncModalRef}>
        <div
          css={css`
            width: 700px;
            padding: 16px;
            background: no-repeat fixed linear-gradient(#282e38, #181a1d);
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            max-height: 80vh;

            @media (max-width: 500px) {
              width: 480px;
            }
          `}
        >
          <button
            onClick={(e) => {
              e.preventDefault()

              calendarSyncModalRef.current?.closeModal()
            }}
            css={css`
              position: absolute;
              top: 30px;
              right: 35px;
            `}
          >
            <Icon name='times' color={colors.ATTENDANCE_GRAY} fontSize={16} />
          </button>
          <div
            css={css`
              display: flex;
              width: 190px;
              height: 130px;
              align-self: center;
            `}
          >
            <img
              src={calendarImg}
              css={css`
                width: 100%;
                height: 100%;
              `}
            />
          </div>
          <div
            css={css`
              align-self: center;
            `}
          >
            <p
              css={css`
                color: ${colors.DEFAULT_FLAIR};
                text-align: center;
                margin: 24px 0;
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
              `}
            >
              Subscribe to Calendar
            </p>
            <p
              css={css`
                color: ${colors.WHITE};
                text-align: center;
                font-size: 14px;
                font-style: normal;
                margin-bottom: 16px;
                font-weight: 500;
              `}
            >
              Bring your team calendar to your phone calendar and get all your
              games/events/practices to show up automatically in one place.
            </p>
            <p
              css={css`
                color: ${colors.ATTENDANCE_GRAY};
                text-align: center;
                font-size: 12px;
                font-style: normal;
                font-weight: 500;
              `}
            >
              To subscribe, select one of the following options, or copy the
              address and subscribe with your favourite email client.
            </p>
          </div>
          <div
            css={css`
              margin-top: 24px;
              display: flex;
              align-items: center;
              align-self: center;
              gap: 24px;

              button {
                gap: 0;
                width: 200px;
                justify-content: center;
              }
            `}
          >
            <Button
              label='GOOGLE'
              labelStyle={css`
                font-family: Rift;
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: normal;
                letter-spacing: 0.9px;
                text-transform: uppercase;
              `}
              labelColor={colors.HEADER}
              onClick={() => {
                const link = document.createElement('a')
                link.href = `https://calendar.google.com/calendar/r?cid=webcal%3A%2F%2F${calendarURL.replace(
                  /^https?:\/\//,
                  ''
                )}`
                link.target = '_blank'
                link.click()
              }}
              icon={
                <Icon
                  name='google'
                  fontSize={18}
                  color={colors.HEADER}
                  faType='fab'
                />
              }
            />
            <Button
              label='APPLE'
              labelStyle={css`
                font-family: Rift;
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: normal;
                letter-spacing: 0.9px;
                text-transform: uppercase;
              `}
              labelColor={colors.HEADER}
              onClick={() => {
                const link = document.createElement('a')
                link.href = `webcal://${calendarURL.replace(
                  /^https?:\/\//,
                  ''
                )}`
                link.target = '_self'
                link.click()
              }}
              icon={
                <Icon
                  name='apple'
                  fontSize={18}
                  color={colors.HEADER}
                  faType='fab'
                />
              }
            />
            <Button
              label='OUTLOOK'
              labelStyle={css`
                font-family: Rift;
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: normal;
                letter-spacing: 0.9px;
                text-transform: uppercase;
              `}
              labelColor={colors.HEADER}
              onClick={() => {
                const link = document.createElement('a')
                link.href = `webcal://${calendarURL.replace(
                  /^https?:\/\//,
                  ''
                )}`
                link.target = '_self'
                link.click()
              }}
              icon={
                <img
                  src={outlookIcon}
                  css={css`
                    width: 20px;
                    height: 20px;
                  `}
                />
              }
            />
          </div>
          <div
            css={css`
              background-color: ${colors.SOFT_STEEL};
              height: 1px;
              margin: 24px 0;
            `}
          />
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
            `}
          >
            <div
              css={css`
                flex: 6;
              `}
            >
              <Input isMockInput mockValue={String(calendarURL)} />
            </div>
            <div
              css={css`
                flex: 1;
              `}
            >
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(calendarURL)
                  setHasCopiedLink(true)
                  setTimeout(() => {
                    setHasCopiedLink(false)
                  }, 2000)
                }}
                label={hasCopiedLink ? 'Copied!' : 'Copy'}
                labelColor={colors.HEADER}
              />
            </div>
          </div>
          <div
            css={css`
              margin-top: 8px;
            `}
          >
            <Button
              antVariant='link'
              label={`or download the ${item?.name_full
                ?.replace(' ', '-')
                ?.toLowerCase()}-calendar.ics file`}
              labelColor={colors.DEFAULT_FLAIR}
              labelStyle={css`
                font-family: Maison Neue;
                font-size: 14px;
                text-transform: none;
              `}
              onClick={() => {
                const link = document.createElement('a')
                link.href = URL.createObjectURL(calendarBlob)
                link.download = `${item?.name_full
                  ?.replace(' ', '-')
                  ?.toLowerCase()}-calendar.ics`
                link.click()
              }}
            />
          </div>
        </div>
      </NewModal>
      <NewModal
        ref={generatePluginTagModalRef}
        shouldCloseOnOverlayClick={false}
      >
        <GeneratePluginModal
          onPressClose={() => {
            generatePluginTagModalRef?.current?.closeModal()
          }}
          scheduleId={item?.id}
          isOrganization={false}
          isTeam={true}
        />
      </NewModal>
      <TeamModal
        isOpen={isTeamModalOpen}
        onClose={() => setIsTeamModalOpen(false)}
        initialState={parsers[ENTITY_TYPES.team]({ ...item, permission })}
        organizationId={item?.organization?.id}
        onSuccess={() => readItem(ENTITY_TYPES.team, TEAM_ID)}
        onDelete={() => {
          history.push(`${ROUTES.ORG_ROOT}/${item?.organization?.id}`)
        }}
        permission={permission}
        shouldAllowEditTeam={shouldEnableAddPlayer}
      />
      {/* Add Player: search existing/new → full-screen roster modal. */}
      <PlayerSearchModal
        isOpen={isPlayerSearchOpen}
        onClose={() => setIsPlayerSearchOpen(false)}
        organizationId={item?.organization?.id}
        accountId={item?.organization?.account_id}
        teamName={item?.name_full}
        excludePlayerIds={rosterPlayerIds}
        onSelectExisting={(player) => {
          setSelectedPlayerId(player?.id)
          setSelectedSearchPlayer({
            id: player?.id,
            name_first: player?.name_first,
            name_last: player?.name_last,
            email: player?.email,
            image: player?.image,
          })
          setIsPlayerSearchOpen(false)
          setIsRosterModalOpen(true)
        }}
        onCreateNew={() => {
          setSelectedPlayerId(undefined)
          setSelectedSearchPlayer(undefined)
          setIsPlayerSearchOpen(false)
          setIsRosterModalOpen(true)
        }}
      />
      {rosterId ? (
        <RosterPlayerModal
          isOpen={isRosterModalOpen}
          playerId={selectedPlayerId}
          searchPlayer={selectedSearchPlayer}
          onClose={() => {
            setIsRosterModalOpen(false)
            setSelectedPlayerId(undefined)
            setSelectedSearchPlayer(undefined)
          }}
          onSuccess={async () => {
            await triggerRefresh()
            setIsRosterModalOpen(false)
            setSelectedPlayerId(undefined)
            setSelectedSearchPlayer(undefined)
          }}
          teamId={TEAM_ID}
          rosterId={rosterId}
          sportId={item?.sport?.id ?? null}
          sportName={item?.sport?.name}
          hasAdminPermission={rosterPermission?.admin}
          requireEmail={requireEmail}
        />
      ) : null}
      <TeamOfficialModal
        isOpen={isOfficialModalOpen}
        onClose={() => setIsOfficialModalOpen(false)}
        teamId={TEAM_ID}
        rosterId={rosterId}
        onSuccess={(data) => {
          setIsOfficialModalOpen(false)
          if (data?.id) {
            setPendingHighlightId(data.id)
          }
          triggerRefresh()
        }}
        sportId={item?.sport?.id}
      />
      <EventModal
        isOpen={eventModal.isOpen}
        onClose={eventModal.close}
        // Deliberately NOT wired to `triggerRefresh`. That bumps `refreshKey`,
        // which is a dependency of GameList's fetch effect — so it re-runs
        // `getItems(1, false)`, replacing every loaded page with page one and
        // dropping the user back to the top of a list they had scrolled
        // through. Saving an event changes no game, and `useEventMutations`
        // already invalidates `events.list`, so the new event appears on its
        // own without disturbing the games beside it.
        // Follow the saved event to its period, so creating a next-week event
        // while reading Past lands the user where that event actually is.
        onSaved={(saved) => followEventRef.current?.(saved)}
        event={eventModal.editingEvent}
        teamId={TEAM_ID}
        // Either tab can open this. `rosterId` is set by the Roster tab;
        // `scheduleRosterId` by GameList from the selected season/division —
        // so whichever tab the user is on supplies the roster the create
        // endpoint needs.
        rosterId={rosterId || scheduleRosterId}
        // ADD from this page creates a TEAM event, so no competition scope.
        // EDIT takes it from the event: a competition event assigned to this
        // team shows up in this list, and without its own scope the form would
        // hide the Location and Team pickers and offer an address block for a
        // venue-backed event.
        scheduleId={resolveEventFormScheduleId({
          event: eventModal.editingEvent,
        })}
      />
    </>
  )
}

// ---- styled ----
// The header row baseline-aligns its children against a 40px uppercase title,
// which would drop a short pill below the text. Centring it on the title's own
// cap height keeps it reading as part of the name.
//
// 4px, not the 10px the header gives its `.header-action` children: the
// favourite star is a 32px button around a smaller glyph, so it already
// carries slack on its right edge. Matching the 10px there stacks on top of
// that slack and visibly detaches the badge from the title group.
const roleBadgeStyles = css`
  align-self: center;
  margin-left: 4px;
`

const mapStateToProps = (state) => {
  return {
    soccerSportId: getSoccerSport(state)?.id,
    hockeySportId: getHockeySport(state)?.id,
    genericSportProperties: getAllGenericSportProperties(state),
    authUserId: state?.auth?.user?.id,
  }
}

export default connect(mapStateToProps)(Team)
